/* eslint complexity: 0 */
import { get, has, merge } from 'lodash';
import {
  CHART_COLORS,
  VEGA_SPEC_AXIS_SETTINGS,
  VEGA_SPEC_LEGEND_SETTINGS,
} from 'constants/chart-spec';
import { CHART_CATEGORIES } from 'constants/chart-categories';
import { INFERRED_TYPE_ENUM } from 'constants/chart-channels';
import { generateVegaTooltip } from 'utils/chart/tooltips';
import {
  generateVegaSortParams,
  generateVegaTypeCoercionTransforms,
} from 'utils/chart/transforms';
import { generateVegaLabelTextExpression } from 'utils/chart/vega-expressions';
import { getScaleMinMaxProps } from 'utils/chart/scale-min-max';

function stackedTransforms() {
  return [
    {
      type: 'impute',
      field: 'x',
      groupby: ['color'],
      key: 'y',
      method: 'value',
      value: 0,
    },
    {
      type: 'stack',
      groupby: ['y'],
      field: 'x',
      sort: { field: ['color'], order: ['descending'] },
      as: ['x_start', 'x_end'],
      offset: 'zero',
    },
  ];
}

function secondaryCoordinate(sortByY, isStack) {
  return sortByY
    ? {
        x2: {
          scale: 'x',
          field: isStack ? 'x_start' : undefined,
          value: isStack ? undefined : 0,
        },
      }
    : {
        y2: { scale: 'y', value: 0 },
      };
}

export default {
  name: 'Area Continuous',
  displayName: 'Continuous',
  order: 2,
  flavour: 'continuous',
  category: CHART_CATEGORIES.AREA,
  iconValue: 'area-continuous',
  channels: [
    {
      name: 'x',
      displayName: 'X Axis',
      required: true,
      channelType: 'value',
      intent: 'x-value',
    },
    {
      name: 'y',
      displayName: 'Y Axis',
      required: true,
      channelType: 'value',
      intent: 'y-value',
      multipleSeries: true,
    },
    {
      name: 'color',
      displayName: 'Series',
      required: false,
      channelType: 'category',
      intent: 'series',
    },
  ],
  specFn: (encodings, customisations) => {
    const sortParams = generateVegaSortParams(encodings);
    const isDate = c => {
      const { inferredType, transformedType } = get(encodings, c, {});
      return (transformedType || inferredType) === INFERRED_TYPE_ENUM.DATE;
    };
    const scaleType = c => (isDate(c) ? 'utc' : 'linear');
    // eslint-disable-next-line no-nested-ternary
    const sortField = isDate('x') ? 'x' : isDate('y') ? 'y' : 'x';
    const sortByY = sortField === 'y';
    const isStack = sortByY && encodings.color;
    const showDataMarkers = get(customisations.options, 'dataMarkers.enabled');
    return {
      autosize: {
        type: 'fit',
        contains: 'padding',
      },
      style: 'cell',
      data: [
        {
          name: 'transformedData',
          source: 'values',
          transform: [
            ...generateVegaTypeCoercionTransforms('x', encodings),
            ...generateVegaTypeCoercionTransforms('y', encodings),
            ...(isStack ? stackedTransforms() : []),
          ],
        },
      ],
      marks: [
        {
          name: 'pathgroup',
          type: 'group',
          from: {
            facet: {
              name: 'faceted_path_main',
              data: 'transformedData',
              groupby: ['color'],
            },
          },
          encode: {
            update: {
              width: { field: { group: 'width' } },
              height: { field: { group: 'height' } },
            },
          },
          marks: [
            {
              name: 'marks',
              type: 'area',
              style: ['area'],
              sort: { field: `datum.${sortField}`, order: 'ascending' },
              from: { data: 'faceted_path_main' },
              encode: {
                update: {
                  fillOpacity: { value: 0.5 },
                  orient: { value: sortByY ? 'horizontal' : 'vertical' },
                  fill: { scale: 'color', field: 'color' },
                  x: { scale: 'x', field: isStack ? 'x_end' : 'x' },
                  y: { scale: 'y', field: 'y' },
                  ...secondaryCoordinate(sortByY, isStack),
                  tooltip: {
                    signal: generateVegaTooltip(encodings, customisations),
                  },
                },
              },
            },
            {
              type: 'symbol',
              from: { data: 'faceted_path_main' },
              encode: {
                update: {
                  x: { scale: 'x', field: isStack ? 'x_end' : 'x' },
                  y: { scale: 'y', field: 'y' },
                  fill: showDataMarkers
                    ? { scale: 'color', field: 'color' }
                    : { value: 'transparent' },
                  stroke: {
                    scale: 'color',
                    field: 'color',
                  },
                  strokeWidth: { value: showDataMarkers ? 1.5 : 0 },
                  size: { value: 30 },
                },
              },
            },
            ...(!get(customisations.options, 'dataValueLabels.enabled')
              ? []
              : [
                  {
                    type: 'text',
                    name: 'valueLabels',
                    from: { data: 'faceted_path_main' },
                    encode: {
                      enter: {
                        text: {
                          signal: generateVegaLabelTextExpression(
                            encodings[sortByY ? 'x' : 'y'],
                            customisations.channels[sortByY ? 'x' : 'y'],
                            { field: sortByY ? 'x' : 'y' }
                          ),
                        },
                        dy: { value: showDataMarkers ? -6 : -3 },
                        x: { scale: 'x', field: isStack ? 'x_end' : 'x' },
                        y: { scale: 'y', field: 'y' },
                        align: { value: 'center' },
                      },
                    },
                  },
                ]),
          ],
        },
      ],
      scales: [
        {
          name: 'x',
          type: scaleType('x'),
          domain: {
            data: 'transformedData',
            [isStack ? 'fields' : 'field']: isStack
              ? ['x_start', 'x_end']
              : 'x',
          },
          range: [0, { signal: 'width' }],
          zero: sortByY,
          ...getScaleMinMaxProps(customisations, 'x'),
        },
        {
          name: 'y',
          type: scaleType('y'),
          domain: { data: 'transformedData', field: 'y' },
          range: [{ signal: 'height' }, 0],
          ...getScaleMinMaxProps(customisations, 'y'),
        },
        {
          name: 'color',
          type: 'ordinal',
          domain: {
            data: 'transformedData',
            field: 'color',
            sort: sortParams.color,
          },
          range:
            get(customisations.options, 'colorDiscrete.value') || CHART_COLORS,
        },
      ],
      axes: [
        merge({}, VEGA_SPEC_AXIS_SETTINGS, {
          scale: 'x',
          orient: 'bottom',
          grid: true,
          title: get(encodings, 'x.axis.title', ''),
          tickCount: { signal: 'ceil(width/40)' },
          encode: {
            labels: {
              update: {
                text: {
                  signal: generateVegaLabelTextExpression(
                    encodings.x,
                    customisations.channels.x,
                    { withDecimals: false, withSpacePadding: true }
                  ),
                },
              },
            },
          },
        }),
        merge({}, VEGA_SPEC_AXIS_SETTINGS, {
          scale: 'y',
          orient: 'left',
          grid: true,
          title: get(encodings, 'y.axis.title', ''),
          tickCount: { signal: 'ceil(height/60)' },
          encode: {
            labels: {
              update: {
                text: {
                  signal: generateVegaLabelTextExpression(
                    encodings.y,
                    customisations.channels.y,
                    { withDecimals: false }
                  ),
                },
              },
            },
          },
        }),
      ],
      legends: !has(encodings, 'color.legend.title')
        ? undefined
        : [
            merge({}, VEGA_SPEC_LEGEND_SETTINGS, {
              fill: 'color',
              title: get(encodings, 'color.legend.title'),
              encode: {
                labels: {
                  update: {
                    text: {
                      signal: generateVegaLabelTextExpression(
                        encodings.color,
                        customisations.channels.color
                      ),
                    },
                  },
                },
              },
            }),
          ],
    };
  },
};
